import type { ReactNode } from 'react';
import type {
  DialogProps as AriaDialogProps,
  ModalOverlayProps as AriaModalOverlayProps,
} from 'react-aria-components';

import {
  Dialog as AriaDialog,
  DialogTrigger as AriaDialogTrigger,
  Modal as AriaModal,
  ModalOverlay as AriaModalOverlay,
} from 'react-aria-components';

import { cx } from '@/utils/cx';

export const DialogTrigger = AriaDialogTrigger;

interface ModalOverlayProps extends AriaModalOverlayProps {
  centered?: boolean;
}

export const ModalOverlay = ({ centered, ...props }: ModalOverlayProps) => {
  return (
    <AriaModalOverlay
      {...props}
      className={(state) =>
        cx(
          'fixed inset-0 z-50 flex min-h-dvh w-full justify-center overflow-y-auto bg-overlay/70 px-4 pt-4 pb-[clamp(16px,8vh,64px)] outline-hidden backdrop-blur-[6px] sm:p-8',
          centered ? 'items-center' : 'items-end sm:items-center',
          state.isEntering && 'duration-300 ease-out animate-in fade-in',
          state.isExiting && 'duration-200 ease-in animate-out fade-out',
          typeof props.className === 'function'
            ? props.className(state)
            : props.className,
        )
      }
    />
  );
};

export const Modal = (props: AriaModalOverlayProps) => (
  <AriaModal
    {...props}
    className={(state) =>
      cx(
        'max-h-full w-full align-middle outline-hidden max-sm:overflow-y-auto max-sm:rounded-xl',
        state.isEntering && 'duration-300 ease-out animate-in zoom-in-95',
        state.isExiting && 'duration-200 ease-in animate-out zoom-out-95',
        typeof props.className === 'function'
          ? props.className(state)
          : props.className,
      )
    }
  />
);

export const Dialog = (props: AriaDialogProps) => (
  <AriaDialog
    {...props}
    className={cx(
      'flex w-full items-center justify-center outline-hidden',
      props.className,
    )}
  />
);

const CONTROLLED_MODAL_SIZE_CLASSES = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-xl',
  '2xl': 'max-w-2xl',
  full: 'max-w-full',
} as const;

export type ControlledModalSize = keyof typeof CONTROLLED_MODAL_SIZE_CLASSES;

interface ControlledModalProps {
  isOpen: boolean;
  onOpenChange?: (isOpen: boolean) => void;
  /**
   * When `false`, outside clicks and Escape do not close the modal.
   * Programmatic close via `onOpenChange` still works.
   * @default true
   */
  allowDismissal?: boolean;
  /**
   * When `true`, children stay mounted when the modal is closed,
   * preserving their internal state across open/close cycles.
   * When `false` (default), children unmount on close and remount
   * fresh on the next open.
   */
  persist?: boolean;
  centered?: boolean;
  /** Constrains the dialog width; overridden by conflicting `className` max-width utilities. */
  size?: ControlledModalSize;
  children: ReactNode;
  className?: string;
  title?: string;
}

/**
 * A controlled modal that combines DialogTrigger, ModalOverlay, Modal,
 * and Dialog into a single component with built-in unmount handling.
 *
 * When `persist` is false (default), the entire modal tree is not rendered
 * when closed, so all children remount with fresh state on each open.
 * When `persist` is true, children stay mounted across open/close cycles.
 */
export function ControlledModal({
  isOpen,
  onOpenChange,
  allowDismissal = true,
  persist = false,
  centered,
  size,
  children,
  className,
  title,
}: ControlledModalProps) {
  if (!persist && !isOpen) return null;

  return (
    <ModalOverlay
      centered={centered}
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      isDismissable={allowDismissal}
      isKeyboardDismissDisabled={!allowDismissal}
    >
      <Modal>
        <Dialog
          aria-label={title}
          className={cx(
            size ? CONTROLLED_MODAL_SIZE_CLASSES[size] : undefined,
            'm-auto',
            className,
          )}
        >
          {children}
        </Dialog>
      </Modal>
    </ModalOverlay>
  );
}
